import { useMutation, type UseMutationOptions, useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ApiClient } from './api.client.js';
import { type CreateNvr, type Nvr } from '../models/index.js';

export const NvrsApiKeys = {
  GET_NVRS: 'GET_NVRS',
  GET_NVR: 'GET_NVR',
  CREATE_NVR: 'CREATE_NVR',
  UPDATE_NVR: 'UPDATE_NVR',
  DELETE_NVR: 'DELETE_NVR',
  TRIGGER_NVR: 'TRIGGER_NVR',
  GET_NVR_SYNC_STATUS: 'GET_NVR_SYNC_STATUS',
} as const;

export type NvrSyncStatus = {
  status: 'idle' | 'queued' | 'running' | 'success' | 'failed';
  error?: string;
  finishedAt?: number;
};

export class NvrsApi extends ApiClient {
  public static getNvrs(patientId?: string): Promise<Nvr[]> {
    return this.http.get('/nvrs', { params: { patient: patientId } }).then((res) => res.data);
  }

  public static getNvr(id: string): Promise<Nvr> {
    return this.http.get(`/nvrs/${id}`).then((res) => res.data);
  }

  public static createNvr(payload: CreateNvr): Promise<Nvr> {
    return this.http.put('/nvrs', payload).then((res) => res.data);
  }

  public static updateNvr(id: string, payload: Partial<CreateNvr>): Promise<Nvr> {
    return this.http.patch(`/nvrs/${id}`, payload).then((res) => res.data);
  }

  public static deleteNvr(id: string): Promise<void> {
    return this.http.delete(`/nvrs/${id}`).then((res) => res.data);
  }

  public static trigger(id: string): Promise<{ status: string }> {
    return this.http.post(`/nvrs/${id}`).then((res) => res.data);
  }

  public static getSyncStatuses(ids: string[]): Promise<Record<string, NvrSyncStatus>> {
    if (ids.length === 0) return Promise.resolve({});
    return this.http.get('/nvrs/sync-status', { params: { ids: ids.join(',') } }).then((res) => res.data);
  }
}

export function useNvrs(queryOptions?: UseQueryOptions<Nvr[]>) {
  return useQuery<Nvr[]>([NvrsApiKeys.GET_NVRS], () => NvrsApi.getNvrs(), queryOptions);
}

export function useNvrsForPatient(patientId?: string, queryOptions?: UseQueryOptions<Nvr[]>) {
  return useQuery<Nvr[]>([NvrsApiKeys.GET_NVRS, patientId], () => NvrsApi.getNvrs(patientId), queryOptions);
}

export function useOneNvr(id?: string, queryOptions?: UseQueryOptions<Nvr>) {
  return useQuery<Nvr>([NvrsApiKeys.GET_NVR, id], () => NvrsApi.getNvr(id as string), { enabled: !!id, ...(queryOptions || {}) });
}

export function useCreateNvr(mutationOptions?: UseMutationOptions<Nvr, unknown, CreateNvr>) {
  return useMutation<Nvr, unknown, CreateNvr>([NvrsApiKeys.CREATE_NVR], (payload) => NvrsApi.createNvr(payload), mutationOptions);
}

export function useUpdateNvr(mutationOptions?: UseMutationOptions<Nvr, unknown, { id: string } & Partial<CreateNvr>>) {
  return useMutation<Nvr, unknown, { id: string } & Partial<CreateNvr>>(
    [NvrsApiKeys.UPDATE_NVR],
    ({ id, ...payload }) => NvrsApi.updateNvr(id, payload),
    mutationOptions,
  );
}

export function useDeleteNvr(mutationOptions?: UseMutationOptions<void, unknown, string>) {
  return useMutation<void, unknown, string>([NvrsApiKeys.DELETE_NVR], (id) => NvrsApi.deleteNvr(id), mutationOptions);
}

export function useTriggerNvr(mutationOptions?: UseMutationOptions<{ status: string }, unknown, string>) {
  return useMutation<{ status: string }, unknown, string>([NvrsApiKeys.TRIGGER_NVR], (id) => NvrsApi.trigger(id), mutationOptions);
}

export function useNvrSyncStatuses(ids: string[], queryOptions?: UseQueryOptions<Record<string, NvrSyncStatus>>) {
  return useQuery<Record<string, NvrSyncStatus>>(
    [NvrsApiKeys.GET_NVR_SYNC_STATUS, ids.slice().sort().join(',')],
    () => NvrsApi.getSyncStatuses(ids),
    {
      enabled: ids.length > 0,
      refetchInterval: (data) => {
        const live = data && Object.values(data).some((s) => s.status === 'queued' || s.status === 'running');
        return live ? 2000 : 15000;
      },
      ...(queryOptions || {}),
    },
  );
}
